import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa';
import VihsMark from '../../components/VihsMark';
import './Legal.css';

const CONTACT_URL = 'https://discord.gg/ScdKWjtkVd';
const INSTAGRAM_URL = 'https://instagram.com/vihsoficial';
const LAST_UPDATED = '14 de agosto de 2026';

const TOC = [
  { id: 'escopo', label: 'Escopo' },
  { id: 'ecossistema', label: 'Ecossistema' },
  { id: 'termos', label: 'Termos de uso' },
  { id: 'studio', label: "Vih's Studio" },
  { id: 'bot', label: "Vih's Bot" },
  { id: 'devhub', label: "Vih's Dev Hub" },
  { id: 'propriedade', label: 'Propriedade intelectual' },
  { id: 'privacidade', label: 'Privacidade' },
  { id: 'comunidade', label: 'Comunidade' },
  { id: 'responsabilidade', label: 'Responsabilidade' },
  { id: 'gerais', label: 'Disposições gerais' },
  { id: 'contato', label: 'Contato' },
];

const fade = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export default function Legal() {
  useEffect(() => {
    document.title = "Termos e Políticas — Vih's";
    return () => {
      document.title = "Vih's — criação digital sob medida";
    };
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-atmosphere" aria-hidden="true">
        <div className="legal-glow" />
        <div className="legal-mesh" />
      </div>

      <header className="legal-header">
        <div className="legal-shell legal-header-inner">
          <Link to="/" className="legal-logo" aria-label="Vih's — página inicial">
            <VihsMark />
            Vih&apos;s
          </Link>
          <Link to="/" className="legal-back">
            <FaArrowLeft aria-hidden="true" />
            Voltar
          </Link>
        </div>
      </header>

      <main className="legal-main">
        <div className="legal-shell legal-layout">
          <motion.aside
            className="legal-toc"
            aria-label="Índice"
            {...fade}
            transition={{ duration: 0.55 }}
          >
            <p className="legal-toc-label">Nesta página</p>
            <nav>
              <ul>
                {TOC.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>{item.label}</a>
                  </li>
                ))}
              </ul>
            </nav>
          </motion.aside>

          <article className="legal-article">
            <motion.header
              className="legal-hero"
              {...fade}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="legal-eyebrow">Documento legal</p>
              <h1>Termos de uso e políticas</h1>
              <p className="legal-lead">
                Este documento rege o uso do site institucional da Vih&apos;s e de todo o
                ecossistema — Studio, Bot e Dev Hub — bem como o tratamento de dados pessoais
                conforme a LGPD.
              </p>
              <p className="legal-updated">Última atualização: {LAST_UPDATED}</p>
            </motion.header>

            <motion.div
              className="legal-body"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <section id="escopo" className="legal-section">
                <h2>1. Aceitação e escopo</h2>
                <p>
                  Ao acessar ou utilizar qualquer propriedade digital da Vih&apos;s, você declara
                  ter lido, compreendido e concordado com estes Termos de Uso e com a Política
                  de Privacidade aqui descritos.
                </p>
                <p>
                  Estes termos aplicam-se, de forma integrada, a:
                </p>
                <ul>
                  <li>Site institucional da Vih&apos;s e páginas vinculadas (incluindo /studio e /termos);</li>
                  <li>Vih&apos;s Studio — braço comercial de criação e entrega de soluções digitais;</li>
                  <li>Vih&apos;s Bot — bot interno operado pela Vih&apos;s em canais oficiais no Discord;</li>
                  <li>Vih&apos;s Dev Hub — produto e espaço para desenvolvedores e criadores;</li>
                  <li>Canais oficiais de comunicação (Discord, Instagram e demais divulgados pela marca).</li>
                </ul>
                <p>
                  Se você não concordar com qualquer parte deste documento, não utilize os
                  serviços, produtos ou canais da Vih&apos;s.
                </p>
              </section>

              <section id="ecossistema" className="legal-section">
                <h2>2. Sobre o ecossistema Vih&apos;s</h2>
                <p>
                  A Vih&apos;s é a marca-mãe do ecossistema. Organiza e apresenta o Studio, o Bot
                  e o Dev Hub sob uma identidade comum de criação digital sob medida.
                </p>
                <h3>2.1 Frentes do ecossistema</h3>
                <ul>
                  <li>
                    <strong>Vih&apos;s Studio:</strong> estúdio comercial para contratação de
                    sistemas sob medida, websites, bots Discord, design e infraestrutura
                    Minecraft, entre outros serviços sob demanda.
                  </li>
                  <li>
                    <strong>Vih&apos;s Bot:</strong> ferramenta interna da marca no Discord,
                    destinada a apoiar operação e atendimento. A criação de bots para terceiros
                    é serviço do Studio, não uso livre do Bot interno.
                  </li>
                  <li>
                    <strong>Vih&apos;s Dev Hub:</strong> produto voltado a desenvolvedores e
                    criadores — recursos, comunidade e infraestrutura digital, conforme
                    funcionalidades disponibilizadas a cada momento.
                  </li>
                </ul>
                <h3>2.2 Natureza dos serviços</h3>
                <p>
                  Conteúdo informativo do site não constitui proposta comercial vinculante.
                  Orçamentos, prazos e escopos de projetos comerciais são definidos em
                  proposta ou contrato específico com o Studio.
                </p>
              </section>

              <section id="termos" className="legal-section">
                <h2>3. Termos de uso gerais</h2>
                <h3>3.1 Elegibilidade</h3>
                <ul>
                  <li>Você deve ter capacidade civil para aceitar estes termos, ou autorização de responsável legal quando aplicável;</li>
                  <li>Para uso de plataformas de terceiros (Discord, Instagram etc.), aplicam-se também os termos dessas plataformas;</li>
                  <li>Idade mínima e regras adicionais podem valer no Dev Hub ou em serviços específicos, quando divulgados.</li>
                </ul>
                <h3>3.2 Conduta proibida</h3>
                <p>É proibido, em qualquer propriedade da Vih&apos;s:</p>
                <ul>
                  <li>Violar direitos autorais, marcas ou outros direitos de propriedade intelectual;</li>
                  <li>Distribuir conteúdo ilegal, ofensivo, discriminatório ou prejudicial;</li>
                  <li>Praticar fraude, engenharia social ou obtenção indevida de dados;</li>
                  <li>Interferir na segurança, disponibilidade ou integridade dos sistemas;</li>
                  <li>Fazer-se passar pela Vih&apos;s ou por membros da equipe;</li>
                  <li>Utilizar automações abusivas, scraping não autorizado ou exploração de falhas;</li>
                  <li>Revender, sublicenciar ou redistribuir ativos da Vih&apos;s sem autorização escrita.</li>
                </ul>
                <h3>3.3 Contas e acesso</h3>
                <p>
                  Quando houver cadastro ou autenticação, você é responsável por manter
                  credenciais em sigilo e por atividades realizadas sob sua conta. A Vih&apos;s
                  pode suspender ou encerrar acessos em caso de violação destes termos.
                </p>
              </section>

              <section id="studio" className="legal-section">
                <h2>4. Vih&apos;s Studio</h2>
                <p>
                  O Studio é o canal comercial para contratação de serviços de criação digital.
                </p>
                <h3>4.1 Contratação</h3>
                <ul>
                  <li>Escopo, prazo, valores e entregáveis são definidos em proposta, briefing ou contrato;</li>
                  <li>Alterações de escopo podem implicar reajuste de prazo e/ou valor;</li>
                  <li>Pagamentos e condições comerciais seguem o combinado por escrito entre as partes.</li>
                </ul>
                <h3>4.2 Entregas e aprovação</h3>
                <p>
                  Entregas são consideradas aceitas após aprovação do cliente ou, na ausência
                  de resposta em prazo razoável informado, conforme previsto na proposta.
                  Correções de erros de execução (bugs) seguem o que for acordado; mudanças
                  de escopo não são garantia gratuita.
                </p>
                <h3>4.3 Propriedade do trabalho contratado</h3>
                <p>
                  Salvo disposição em contrário no contrato, direitos sobre o material
                  desenvolvido especificamente para o cliente são transferidos ou licenciados
                  conforme acordado. Ferramentas, bibliotecas, frameworks e know-how prévios
                  da Vih&apos;s permanecem de propriedade da Vih&apos;s.
                </p>
                <h3>4.4 Terceiros e infraestrutura</h3>
                <p>
                  Serviços que dependam de plataformas de terceiros (hospedagem, Discord,
                  Minecraft, provedores de API etc.) estão sujeitos às políticas desses
                  terceiros. A Vih&apos;s não se responsabiliza por interrupções, mudanças de
                  API ou banimentos originados exclusivamente nessas plataformas.
                </p>
              </section>

              <section id="bot" className="legal-section">
                <h2>5. Vih&apos;s Bot</h2>
                <p>
                  O Vih&apos;s Bot é um bot interno da marca, operado em servidores e canais
                  oficiais da Vih&apos;s no Discord.
                </p>
                <ul>
                  <li>Não constitui produto de uso geral para instalação em servidores de terceiros sem autorização;</li>
                  <li>Comandos, permissões e dados processados pelo Bot destinam-se à operação e ao atendimento da marca;</li>
                  <li>Abuso, spam ou tentativa de exploração do Bot podem resultar em remoção de acesso e medidas adicionais;</li>
                  <li>Bots personalizados para outros servidores são contratados via Vih&apos;s Studio.</li>
                </ul>
              </section>

              <section id="devhub" className="legal-section">
                <h2>6. Vih&apos;s Dev Hub</h2>
                <p>
                  O Dev Hub é o produto do ecossistema voltado a desenvolvedores e criadores.
                  Funcionalidades podem evoluir; o acesso a recursos específicos pode exigir
                  cadastro, convite ou termos complementares.
                </p>
                <ul>
                  <li>Conteúdo e recursos disponibilizados devem ser usados de forma lícita e ética;</li>
                  <li>Contribuições de usuários (quando houver) devem respeitar direitos de terceiros;</li>
                  <li>A Vih&apos;s pode moderar, remover conteúdo ou restringir contas que violem estas regras;</li>
                  <li>Recursos em prévia, beta ou “em breve” podem ser alterados ou descontinuados sem aviso prévio obrigatório além do razoável.</li>
                </ul>
              </section>

              <section id="propriedade" className="legal-section">
                <h2>7. Propriedade intelectual</h2>
                <h3>7.1 Marca e conteúdo da Vih&apos;s</h3>
                <p>
                  Nome, logotipo, identidade visual, textos, código, designs e demais materiais
                  da Vih&apos;s e de suas frentes (Studio, Bot, Dev Hub) são protegidos por
                  legislação de propriedade intelectual. É vedada a reprodução, adaptação ou
                  uso comercial sem autorização prévia por escrito.
                </p>
                <h3>7.2 Conteúdo do usuário</h3>
                <p>
                  Ao enviar conteúdo a canais ou produtos da Vih&apos;s (mensagens, arquivos,
                  feedback, materiais em comunidade), você declara possuir os direitos
                  necessários e concede à Vih&apos;s licença não exclusiva, mundial e gratuita
                  para usar, exibir e armazenar esse conteúdo na medida necessária à operação
                  dos serviços — sem transferir a titularidade, salvo acordo em contrário.
                </p>
                <h3>7.3 Denúncias</h3>
                <p>
                  Alegações de violação de direitos autorais ou marca devem ser encaminhadas
                  pelos canais oficiais de contato, com identificação do denunciante,
                  descrição da obra e URL ou local da suposta infração.
                </p>
              </section>

              <section id="privacidade" className="legal-section">
                <h2>8. Política de privacidade (LGPD)</h2>
                <p>
                  A Vih&apos;s trata dados pessoais em conformidade com a Lei Geral de Proteção
                  de Dados (Lei nº 13.709/2018) e demais normas aplicáveis no Brasil.
                </p>
                <h3>8.1 Dados que podemos tratar</h3>
                <ul>
                  <li>Dados de contato fornecidos voluntariamente (nome, e-mail, usuário Discord/Instagram);</li>
                  <li>Dados de briefing e projeto necessários à execução de serviços do Studio;</li>
                  <li>Dados técnicos de navegação (endereço IP, tipo de dispositivo, páginas acessadas), quando coletados por meios legítimos;</li>
                  <li>Registros de interação em canais oficiais e, quando aplicável, no Dev Hub ou no Bot;</li>
                  <li>Dados de pagamento processados por intermediários — a Vih&apos;s não armazena dados completos de cartão quando o processamento for feito por gateway de terceiros.</li>
                </ul>
                <h3>8.2 Finalidades</h3>
                <ul>
                  <li>Prestar e melhorar serviços do ecossistema;</li>
                  <li>Comunicar sobre projetos, propostas e suporte;</li>
                  <li>Garantir segurança, prevenção a fraude e cumprimento legal;</li>
                  <li>Cumprir obrigações contratuais e legais.</li>
                </ul>
                <h3>8.3 Bases legais</h3>
                <p>
                  O tratamento ocorre, conforme o caso, com base em execução de contrato,
                  legítimo interesse (com avaliação de impacto quando cabível), cumprimento
                  de obrigação legal ou consentimento do titular.
                </p>
                <h3>8.4 Compartilhamento</h3>
                <p>
                  Dados podem ser compartilhados com prestadores essenciais (hospedagem,
                  ferramentas de comunicação, gateways de pagamento) sob obrigação de
                  confidencialidade, ou quando exigido por autoridade competente. Não
                  vendemos dados pessoais.
                </p>
                <h3>8.5 Direitos do titular</h3>
                <p>
                  Nos termos da LGPD, você pode solicitar confirmação de tratamento, acesso,
                  correção, anonimização, portabilidade, eliminação (quando cabível),
                  informação sobre compartilhamentos e revogação de consentimento. Para
                  exercer direitos, utilize os canais oficiais indicados neste documento.
                </p>
                <h3>8.6 Retenção e segurança</h3>
                <p>
                  Mantemos dados pelo tempo necessário às finalidades ou exigências legais.
                  Adotamos medidas técnicas e organizacionais razoáveis; nenhum sistema é
                  absolutamente isento de risco.
                </p>
                <h3>8.7 Cookies e serviços de terceiros</h3>
                <p>
                  O site pode utilizar cookies ou tecnologias similares essenciais ao
                  funcionamento. Plataformas externas (Discord, Instagram) possuem políticas
                  próprias — recomendamos a leitura desses documentos.
                </p>
              </section>

              <section id="comunidade" className="legal-section">
                <h2>9. Comunidade e canais oficiais</h2>
                <p>Esperamos respeito mútuo em todos os espaços da Vih&apos;s. Em especial:</p>
                <ul>
                  <li>Não toleramos assédio, discurso de ódio ou conteúdo ilícito;</li>
                  <li>Não solicite ou compartilhe senhas e dados sensíveis em canais públicos;</li>
                  <li>A equipe da Vih&apos;s nunca pedirá senhas fora dos fluxos oficiais autenticados.</li>
                </ul>
                <p className="legal-note">
                  <strong>Canais oficiais:</strong> Discord (
                  <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
                    discord.gg/ScdKWjtkVd
                  </a>
                  ), Instagram (
                  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                    @vihsoficial
                  </a>
                  ) e demais endereços publicados no site da Vih&apos;s.
                </p>
              </section>

              <section id="responsabilidade" className="legal-section">
                <h2>10. Limitação de responsabilidade</h2>
                <ul>
                  <li>Os serviços são fornecidos “como estão”, no limite permitido pela lei;</li>
                  <li>A Vih&apos;s não garante disponibilidade ininterrupta de sites, bots ou produtos;</li>
                  <li>Não nos responsabilizamos por danos indiretos, lucros cessantes ou perdas decorrentes de uso indevido, força maior ou falhas de terceiros;</li>
                  <li>Em projetos do Studio, a responsabilidade contratual segue o instrumento específico firmado entre as partes;</li>
                  <li>Links externos são de responsabilidade de seus operadores.</li>
                </ul>
              </section>

              <section id="gerais" className="legal-section">
                <h2>11. Disposições gerais</h2>
                <h3>11.1 Alterações</h3>
                <p>
                  Podemos atualizar estes Termos e Políticas periodicamente. A data de
                  “última atualização” será revista. O uso continuado após a publicação
                  constitui aceitação das mudanças, salvo quando a lei exigir consentimento
                  específico.
                </p>
                <h3>11.2 Rescisão</h3>
                <p>
                  Podemos restringir ou encerrar o acesso a produtos e canais em caso de
                  violação destes termos, risco à segurança ou obrigação legal, sem prejuízo
                  de outras medidas cabíveis.
                </p>
                <h3>11.3 Lei aplicável</h3>
                <p>
                  Este documento é regido pelas leis da República Federativa do Brasil.
                  Fica eleito o foro da comarca do domicílio do titular da Vih&apos;s, salvo
                  disposição legal de foro privilegiado do consumidor.
                </p>
                <h3>11.4 Independência das cláusulas</h3>
                <p>
                  Se qualquer disposição for considerada inválida, as demais permanecem em vigor.
                </p>
              </section>

              <section id="contato" className="legal-section legal-section-contact">
                <h2>12. Contato</h2>
                <p>
                  Dúvidas sobre estes Termos, privacidade ou o ecossistema Vih&apos;s:
                </p>
                <ul>
                  <li>
                    <strong>Discord:</strong>{' '}
                    <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
                      Comunidade oficial Vih&apos;s
                    </a>
                  </li>
                  <li>
                    <strong>Instagram:</strong>{' '}
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                      @vihsoficial
                    </a>
                  </li>
                </ul>
                <p>
                  Para projetos comerciais, utilize o fluxo de solicitação disponível no site
                  ou no Discord oficial.
                </p>
                <div className="legal-cta-row">
                  <Link to="/" className="legal-btn legal-btn-primary">
                    <FaArrowLeft aria-hidden="true" />
                    Voltar ao início
                  </Link>
                  <Link to="/studio" className="legal-btn legal-btn-ghost">
                    Conhecer o Studio
                  </Link>
                </div>
              </section>
            </motion.div>
          </article>
        </div>
      </main>

      <footer className="legal-footer">
        <div className="legal-shell legal-footer-inner">
          <p>© {new Date().getFullYear()} Vih&apos;s. Todos os direitos reservados.</p>
          <p>Criação digital sob medida.</p>
        </div>
      </footer>
    </div>
  );
}
